import React, {
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  FloatingPortal,
  type Placement,
  autoUpdate,
  flip,
  offset as offsetMiddleware,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
} from '@floating-ui/react';
import { useSpring, motion } from 'framer-motion';
import { Colors, Spacings } from 'styles';
import { transparentize } from 'color2k';

import { env } from 'config/env';
import Text from '../Text';
import { css } from 'styled-components';
import { rootTheme } from 'theme/rootTheme';
import { parseTrigger } from '../parseTrigger';

const springConfig = {
  damping: 80,
  stiffness: 800,
} as const;

// Disable the entrance scale animation during storyshots to improve stability.
const ANIMATION_DISABLED = env.STORYBOOK_STORYSHOTS === 'true';
const INITIAL_SCALE = ANIMATION_DISABLED ? 1 : 0.85;

const MotionDiv = motion.ul;

// Consumers of the render prop only use `.hide()`/`.unmount()`, so the instance passed to it
// exposes just those two methods.
export type MenuInstance = {
  hide: () => void;
  unmount: () => void;
};

type Props = {
  render: (instance: MenuInstance) => React.ReactElement;
  disabled?: boolean;
  children?: React.ReactNode;
  reference?: Element;
  // Portal target for the floating menu. Defaults to document.body.
  appendTo?: Element | (() => Nullable<Element>);
  offset?: [number, number];
  placement?: Placement;
  visible?: boolean;
  trigger?: string;
  hideOnClick?: boolean | 'toggle';
  onClickOutside?: (instance: MenuInstance) => void;
  onMount?: (instance: MenuInstance) => void;
  // Called before the menu opens; returning `false` cancels the open.
  onShow?: (instance: MenuInstance) => void | boolean;
  onHide?: (instance: MenuInstance) => void;
  onHidden?: (instance: MenuInstance) => void;
  width?: string | number;
  css?: unknown;
  className?: string;
  strategy?: 'fixed' | 'absolute';
  ['data-testid']?: string;
};

function MenuComponent(
  {
    visible,
    render,
    children,
    reference,
    offset = [0, 0],
    placement = 'bottom',
    strategy = 'absolute',
    trigger,
    hideOnClick,
    appendTo,
    disabled = false,
    onMount,
    onHidden,
    onHide,
    onShow,
    onClickOutside,
    width,
    className,
    'data-testid': dataTestid,
  }: Props,
  ref: React.Ref<Element>
) {
  const opacity = useSpring(0, springConfig);
  const scale = useSpring(INITIAL_SCALE, springConfig);
  const scrollTopRef = useRef<number | undefined>();
  const [menu, setMenu] = useState<HTMLElement | null>(null);

  const isControlled = visible !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  // Controlled menus derive `open` directly from `visible` (single source of
  // truth — no internal mirror that could desync); uncontrolled menus own
  // their open state internally.
  const open = !disabled && (isControlled ? Boolean(visible) : internalOpen);

  const instanceRef = useRef<MenuInstance>({ hide: () => {}, unmount: () => {} });

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next && onShow?.(instanceRef.current) === false) return;
      // Only the uncontrolled path owns open state; controlled callers drive it
      // via `visible`, so we just notify them on close via onClickOutside.
      if (!isControlled) setInternalOpen(next);
      if (!next) onClickOutside?.(instanceRef.current);
    },
    [isControlled, onShow, onClickOutside]
  );

  instanceRef.current = {
    hide: () => handleOpenChange(false),
    unmount: () => handleOpenChange(false),
  };

  const [skidding, distance] = offset;

  const {
    refs,
    floatingStyles,
    context,
    placement: resolvedPlacement,
  } = useFloating({
    open,
    onOpenChange: handleOpenChange,
    placement,
    strategy,
    whileElementsMounted: autoUpdate,
    middleware: [
      offsetMiddleware({ mainAxis: distance, crossAxis: skidding }),
      flip(),
      shift({ padding: 8 }),
      size({
        padding: { bottom: 15 },
        apply({ availableHeight, elements }) {
          const inner = elements.floating.firstElementChild as HTMLElement | null;
          if (inner != null) inner.style.maxHeight = `${Math.max(0, availableHeight)}px`;
        },
      }),
    ],
  });

  // Controlled menus are driven entirely by `visible`; uncontrolled menus use their trigger
  // (default hover + focus). safePolygon lets the cursor travel from the reference to the
  // interactive menu without closing.
  const triggers = parseTrigger(trigger);
  const triggersEnabled = !disabled && !isControlled;
  const hover = useHover(context, {
    enabled: triggersEnabled && triggers.mouseenter,
    handleClose: safePolygon(),
  });
  const focus = useFocus(context, { enabled: triggersEnabled && triggers.focus });
  const click = useClick(context, {
    enabled: triggersEnabled && triggers.click,
    toggle: hideOnClick !== false,
  });
  const dismiss = useDismiss(context, { enabled: !disabled });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss]);

  // Use the external reference as the dom reference (not just a position
  // anchor) so useDismiss treats clicks on it as reference — not outside —
  // presses. Otherwise a toggle button that is the reference would be dismissed
  // by useDismiss and immediately reopened by its own onClick.
  useEffect(() => {
    if (reference != null) refs.setReference(reference);
  }, [reference, refs]);

  // Rendering is gated on `open` directly (no deferred mount state), so the
  // menu unmounts synchronously on close — keeping React state updates inside
  // the triggering act() and never leaving a detached menu root behind. The
  // entrance fade/scale is driven by the springs (motion values, not React
  // state). onMount/onHide(/onHidden) fire on the open transition; callbacks
  // are read through a ref so inline-identity callbacks don't re-run the effect.
  const lifecycleRef = useRef({ onMount, onHide, onHidden });
  lifecycleRef.current = { onMount, onHide, onHidden };
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open === prevOpen.current) return;
    prevOpen.current = open;

    if (open) {
      lifecycleRef.current.onMount?.(instanceRef.current);
      scale.set(1);
      opacity.set(1);
    } else {
      lifecycleRef.current.onHide?.(instanceRef.current);
      lifecycleRef.current.onHidden?.(instanceRef.current);
      scale.set(INITIAL_SCALE);
      opacity.set(0);
    }
  }, [open, scale, opacity]);

  // Restore scroll position when the menu re-mounts for a new reference.
  useEffect(() => {
    if (menu) menu.scrollTop = scrollTopRef.current ?? 0;
  }, [reference, menu]);

  const menuWidth =
    width != null ? `${String(width)}${typeof width === 'number' ? 'px' : ''}` : 'auto';

  const childRef = useMergeRefs([refs.setReference, ref, (children as any)?.ref ?? null]);
  const referenceChild = isValidElement(children)
    ? cloneElement(children as any, {
        ref: childRef,
        ...getReferenceProps((children as any).props),
      })
    : children;

  // Default (no appendTo): render the menu inline as a sibling of the reference so it stays in
  // the same DOM subtree as its trigger (matching the legacy parent-append default). Consumers
  // that need a different stacking or clipping context opt into a portal via appendTo.
  const portalRoot = (
    typeof appendTo === 'function' ? appendTo() : appendTo
  ) as Nullable<HTMLElement>;

  const floating = open ? (
    <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 9999 }}>
      <MotionDiv
        ref={setMenu}
        {...getFloatingProps()}
        data-placement={resolvedPlacement}
        style={{ opacity, scale }}
        onScroll={() => {
          scrollTopRef.current = menu?.scrollTop ?? 0;
        }}
        className={className}
        data-testid={dataTestid}
        css={`
          background-color: ${Colors.gray3};
          color: ${Colors.gray10};
          border-radius: ${rootTheme.radius.xs}px;
          overflow-y: auto;
          box-shadow: 0 ${rootTheme.radius.xs}px 1rem ${transparentize(Colors.gray1, 0.5)};
          width: ${menuWidth};
          display: flex;
          flex-direction: column;
          padding: 0;
          margin: 0;
          padding-top: ${Spacings.xsmall}em;
          padding-bottom: ${Spacings.xsmall}em;

          &[data-placement='top'] {
            transform-origin: bottom;
          }
          &[data-placement='top-start'] {
            transform-origin: left bottom;
          }
          &[data-placement='top-end'] {
            transform-origin: right bottom;
          }

          &[data-placement='bottom'] {
            transform-origin: top;
          }
          &[data-placement='bottom-start'] {
            transform-origin: left top;
          }
          &[data-placement='bottom-end'] {
            transform-origin: right top;
          }

          &[data-placement='left'] {
            transform-origin: right;
          }
          &[data-placement='left-start'] {
            transform-origin: right top;
          }
          &[data-placement='left-end'] {
            transform-origin: right bottom;
          }

          &[data-placement='right'] {
            transform-origin: left;
          }
          &[data-placement='right-start'] {
            transform-origin: left top;
          }
          &[data-placement='right-end'] {
            transform-origin: left bottom;
          }
        `}
      >
        {render?.(instanceRef.current) ?? null}
      </MotionDiv>
    </div>
  ) : null;

  return (
    <>
      {referenceChild}
      {floating != null && appendTo != null ? (
        <FloatingPortal root={portalRoot}>{floating}</FloatingPortal>
      ) : (
        floating
      )}
    </>
  );
}

export const Menu = forwardRef(MenuComponent);

type MenuItemProps = React.ComponentPropsWithoutRef<'li'> & {
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: (e: React.SyntheticEvent<HTMLLIElement>) => unknown;
  className?: string;
  css?: unknown; // if you really need this escape hatch
  title?: string;
};

export const MenuItem = React.forwardRef<HTMLLIElement, MenuItemProps>(
  ({ children, disabled, className, ...props }, ref) => (
    <li
      {...props}
      ref={ref}
      role="button"
      tabIndex={disabled === true ? -1 : 0}
      aria-disabled={disabled === true ? true : undefined}
      css={css`
        padding: 0;
        background-color: transparent;
        cursor: ${disabled === true ? 'default' : 'pointer'};
        text-align: left;
        list-style: none;
      `}
    >
      <Text
        variant="body1"
        css={css`
          padding: ${Spacings.xsmall}em ${Spacings.small}em;
          display: flex;
          align-items: center;
          color: ${disabled === true ? Colors.gray6 : Colors.gray10};
          &:hover,
          &:focus {
            background-color: ${disabled === true ? 'transparent' : Colors.gray4};
          }
          transition:
            background-color 0.2s ease-in-out,
            color 0.2s ease-in-out;
        `}
        className={className}
      >
        {children}
      </Text>
    </li>
  )
);
MenuItem.displayName = 'MenuItem';

export const MenuHeader: React.ComponentType<{
  children: React.ReactNode;
}> = forwardRef(({ children, ...props }, ref) => (
  <MenuItem disabled>
    {/* @ts-expect-error [EN-7967] - TS2769 - No overload matches this call. */}
    <Text variant="body2" color="secondary" ref={ref}>
      {children}
    </Text>
  </MenuItem>
));
MenuHeader.displayName = 'MenuHeader';
